import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomCheckbox, CustomInput } from "@components/Forms";
import { TrashIcon } from "@components/Icons";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import Spin from "@components/UI/Spin";
import useEscapeKey from "@hooks/useEscapeKey";

const ClarityDynamicParamsModal = ({
  isOpen,
  onClose,
  rows = [],
  onAddRow,
  onUpdateRow,
  onDeleteRow,
  onToggleRow,
  onApply,
  isApplyPending = false,
  "data-testid": dataTestId = "clarity-dynamic-params-modal",
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${colors.bgBlurModal} backdrop-blur-[1px]`}
      data-testid={`${dataTestId}-overlay`}
    >
      <div
        className={`relative w-full max-w-5xl ${colors.bgModal} rounded-lg shadow-xl`}
        onClick={(event) => event.stopPropagation()}
        data-testid={dataTestId}
      >
        <div className={`px-6 py-4 border-b ${colors.borderPrimary}`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className={`text-lg font-semibold uppercase ${colors.textPrimary}`} data-testid={`${dataTestId}-title`}>
                {t("preferences.clarity-dynamic-params", "Clarity Dynamic Params")}
              </h3>
              <p className={`mt-1 text-sm ${colors.textSecondary}`}>
                {t(
                  "preferences.clarity-dynamic-params-desc",
                  "Configure request params for Clarity integration. Toggle checkboxes to include values in backend payload.",
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={isApplyPending}
              className={`mt-1 rounded p-1 ${colors.textSecondary} hover:${colors.textActive} disabled:opacity-50`}
              aria-label={t("common.close", "Close")}
              data-testid={`${dataTestId}-close-button`}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6" data-testid={`${dataTestId}-content`}>
          <div className={`rounded-lg border ${colors.borderPrimary} overflow-hidden`}>
            <div className={`grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2.75rem_2.25rem] gap-x-2 gap-y-2 px-4 py-3 border-b ${colors.borderPrimary} ${colors.bgSecondary}`}>
              <p className={`text-xs font-semibold uppercase ${colors.textSecondary}`} data-testid={fieldLabelTestId("param-name-header")}>
                {t("preferences.param-name", "Param Name")}
              </p>
              <p className={`text-xs font-semibold uppercase ${colors.textSecondary}`} data-testid={fieldLabelTestId("param-value-header")}>
                {t("preferences.param-value", "Path / Value")}
              </p>
              <p className={`text-center self-center min-w-0`}>
                <span className="sr-only">{t("common.enabled", "Enabled")}</span>
              </p>
              <p className={`text-center self-center min-w-0`}>
                <span className="sr-only">{t("common.action", "Action")}</span>
              </p>
            </div>

            <div
              className={`overflow-y-auto ${rows.length > 6 ? "max-h-[30rem]" : "max-h-none"}`}
            >
              {rows.length === 0 ? (
                <div className={`px-4 py-6 text-sm ${colors.textSecondary}`} data-testid={`${dataTestId}-empty-state`}>
                  {t("preferences.no-params-yet", "No params yet. Click Add Param to start.")}
                </div>
              ) : (
                rows.map((row, index) => (
                  <div
                    key={row.id}
                    className={`grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2.75rem_2.25rem] gap-x-2 gap-y-2 px-4 py-3 border-b last:border-b-0 ${colors.borderPrimary}`}
                    data-testid={`${dataTestId}-field-row-${index}-container`}
                  >
                    <CustomInput
                      type="text"
                      value={row.paramKey}
                      onChange={(event) => onUpdateRow(row.id, "paramKey", event.target.value)}
                      placeholder={t("preferences.param-name-placeholder", "e.g. securities")}
                      disabled={isApplyPending}
                      data-testid={fieldInputTestId(`row-${index}-param-name`)}
                    />
                    <CustomInput
                      type="text"
                      value={row.paramValue}
                      onChange={(event) => onUpdateRow(row.id, "paramValue", event.target.value)}
                      placeholder={t("preferences.param-value-placeholder", "e.g. critical,high,medium,low")}
                      disabled={isApplyPending}
                      data-testid={fieldInputTestId(`row-${index}-param-value`)}
                    />
                    <div className="h-10 flex items-center justify-center min-w-0">
                      <CustomCheckbox
                        checked={row.checked}
                        onChange={() => onToggleRow(row.id)}
                        disabled={isApplyPending}
                        data-testid={fieldInputTestId(`row-${index}-enabled`)}
                      />
                    </div>
                    <div className="h-10 flex items-center justify-center min-w-0">
                      <button
                        type="button"
                        onClick={() => onDeleteRow(row.id)}
                        disabled={isApplyPending}
                        className={`rounded p-1 disabled:opacity-50 ${colors.iconDelete} ${colors.hoverTextDangerLight}`}
                        aria-label={t("common.delete-button", "Delete")}
                        data-testid={fieldInputTestId(`row-${index}-delete`)}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="mt-4">
            <OutlineButton data-testid={`${dataTestId}-add-param-button`} onClick={onAddRow} disabled={isApplyPending}>
              {t("preferences.add-param", "+ Add Param")}
            </OutlineButton>
          </div>
        </div>

        <div className={`px-6 py-4 border-t ${colors.borderPrimary} flex justify-end gap-3`} data-testid={`${dataTestId}-footer`}>
          <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={onClose} disabled={isApplyPending}>
            {t("common.cancel-button", "Cancel")}
          </OutlineButton>
          <PrimaryButton data-testid={`${dataTestId}-confirm-button`} onClick={onApply} disabled={isApplyPending}>
            {isApplyPending && <Spin className="mr-2" />}
            {t("common.apply", "Apply")}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default ClarityDynamicParamsModal;
